import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { FormControl, InputAdornment, Skeleton } from "@mui/material";

import { getSelectedValues } from "../../../utils/helpers";

import {
  useDynamicMenuPosition,
  useSelect,
  useSetValueFromQuery,
  useWidth,
} from "../../../hooks";

import { SelectIndicator } from "../../../assets/img/svg/SelectIndicator";
import { WorldIcon } from "../../common/WorldIcon";
import CircleIconFlag from "../CircleIconFlag";
import { style } from "./style";

import {
  BoxIcon,
  CountryName,
  IconWrapper,
  Label,
  ListSubheaderStyled,
  MenuItemStyled,
  RenderComponent,
  SelectStyled,
  TextFieldStyled,
  Wrapper,
} from "./SelectAndSearch.styled";

export const SelectAndSearch = memo(
  ({
    selectName,
    data,
    isLoading = false,
    placeholder,
    handleChange,
    name,
    type,
    refSelect = null,
    location,
    addStyle,
    multiple,
    showFlag,
    queryValue,
    allOption,
    selectOption = "id",
    fingerPrintOption,
  }) => {
    const isPartOfFilter = type === "filter";
    const { withElement, parentRef } = useWidth();
    const [, setSearchParams] = useSearchParams();
    const { t } = useTranslation();

    const {
      selectedItem,
      isOpen,
      setIsOpen,
      displayedData,
      handleSelectionChange,
      handleSearchTextChange,
      selectIdInput,
      selectIdSelect,
      setSearchText,
      setSelectedItem,
    } = useSelect({
      data,
      selectName,
      name,
      handleChange,
      allOption,
      selectOption,
      fingerPrintOption,
    });

    useSetValueFromQuery(
      data,
      queryValue,
      setSelectedItem,
      allOption,
      selectOption,
      selectedItem,
    );

    const { dynamicLocation, isOpenUpwards } = useDynamicMenuPosition(
      isOpen,
      parentRef,
      320,
    );

    const locationSelect = location || dynamicLocation;

    const { value, renderSelectedValues } = getSelectedValues(
      selectedItem,
      selectName,
      multiple,
      showFlag,
      selectOption,
    );

    const handleGetAllProxies = () => {
      setSearchParams(prevParams => {
        const newParams = new URLSearchParams(prevParams);
        newParams.set("page", "1");
        return newParams;
      });
    };

    return (
      <Wrapper ref={parentRef}>
        <FormControl fullWidth>
          <Label
            addStyle={addStyle?.label}
            value={selectedItem}
            shrink={false}
            id={selectIdInput}
            isOpen={isOpen}
          >
            {placeholder}
          </Label>
          <SelectStyled
            autoComplete="off"
            addStyle={addStyle?.select}
            IconComponent={() => (
              <BoxIcon isPartOfFilter={isPartOfFilter} isOpen={isOpen}>
                <SelectIndicator />
              </BoxIcon>
            )}
            ref={refSelect}
            multiple={multiple}
            isPartOfFilter={isPartOfFilter}
            isOpen={isOpen}
            labelId={selectIdInput}
            id={selectIdSelect}
            name={name}
            value={value}
            label={placeholder}
            onChange={e => handleSelectionChange(e, multiple)}
            onOpen={() => {
              setIsOpen(true);
            }}
            onClose={() => {
              setIsOpen(false);
              setSearchText("");
            }}
            MenuProps={{
              ...locationSelect,
              autoFocus: false,
              disableEnforceFocus: true,
              disableRestoreFocus: true,
              PaperProps: {
                sx: style.selectPaperProps(withElement, isOpenUpwards),
              },
            }}
            renderValue={() => (
              <RenderComponent
                variant="form"
                isPartOfFilter={isPartOfFilter}
                isOpen={isOpen}
                value={selectedItem}
              >
                {showFlag && renderSelectedValues.countryCode ? (
                  <IconWrapper>
                    <CircleIconFlag
                      className={renderSelectedValues.countryCode}
                    />
                    <CountryName>{renderSelectedValues.value}</CountryName>
                  </IconWrapper>
                ) : (
                  renderSelectedValues.value
                )}
              </RenderComponent>
            )}
          >
            <ListSubheaderStyled>
              <TextFieldStyled
                autoFocus={true}
                placeholder={t("freeProxyServer.table.search")}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start"></InputAdornment>
                  ),
                }}
                onChange={handleSearchTextChange}
                onKeyDown={e => {
                  if (e.key !== "Escape") {
                    e.stopPropagation();
                  }
                }}
                onClick={e => e.stopPropagation()}
              />
            </ListSubheaderStyled>

            {fingerPrintOption && (
              <MenuItemStyled
                tabIndex={0}
                value={fingerPrintOption[selectOption]}
              >
                {fingerPrintOption.name}
              </MenuItemStyled>
            )}

            {allOption && (
              <MenuItemStyled
                tabIndex={0}
                value={allOption[selectOption]}
                onClick={handleGetAllProxies}
              >
                <IconWrapper>
                  {showFlag && (
                    <img
                      width={24}
                      height={24}
                      src="/assets/images/m_world.png"
                      alt={"world"}
                    />
                  )}
                  <CountryName> {allOption.name} </CountryName>{" "}
                </IconWrapper>
              </MenuItemStyled>
            )}

            {isLoading ? (
              <Skeleton variant="rectangular" height={50} />
            ) : (
              displayedData?.map(item => (
                <MenuItemStyled
                  tabIndex={0}
                  key={item?.id}
                  value={item[selectOption]}
                >
                  {showFlag ? (
                    <IconWrapper>
                      {item?.alpha2 || item?.isoCode ? (
                        <CircleIconFlag
                          className={
                            (item.alpha2 || item.isoCode)?.toLowerCase() || ""
                          }
                        />
                      ) : (
                        <WorldIcon />
                      )}
                      <CountryName>{item[selectName]}</CountryName>
                    </IconWrapper>
                  ) : (
                    item[selectName]
                  )}
                </MenuItemStyled>
              ))
            )}
            {!selectedItem && (
              <MenuItemStyled hidden value={""}></MenuItemStyled>
            )}
          </SelectStyled>
        </FormControl>
      </Wrapper>
    );
  },
);
